import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import { UserMetadataService } from '@core/services/user-metadata.service';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
import {
  DvDropdownClientSideElement,
  DvPhoneInputElement,
  DvSpaceElement,
  DvTextboxElement,
} from '@devinco/ui/legacy';
import { RoleDto } from '@pages/settings-page/access-control-settings/roles/grid/interfaces';
import { activeRecordFilter } from '@shared/constants/active-record-filter';
import { Entity } from '@shared/constants/entity';
import { QuickFilterModel } from '@shared/constants/quick-filters/quick-filter-models';
import { UserDto } from '@shared/interfaces/user.model';

@Injectable({ providedIn: 'root' })
export class UserInformationTabService {
  private readonly restApi = inject(RestApiService);
  private userMetadataService = inject(UserMetadataService);

  getPersonalInfoElements = (): DvDynamicFormElement[] => [
    new DvTextboxElement({
      colspan: 6,
      key: 'Username',
      label: 'User.Username',
      type: 'email',
      disabled: true,
      validators: [Validators.email, Validators.required],
    }),
    new DvTextboxElement({
      colspan: 6,
      key: 'UserDescription',
      label: 'User.Name',
    }),
    new DvPhoneInputElement({
      colspan: 6,
      key: 'Mobile',
      label: 'User.Mobile',
      defaultCountryCode: this.userMetadataService.getUserCountry(),
    }),
    new DvTextboxElement({
      colspan: 6,
      key: 'EmployeeIDText',
      label: 'User.EmployeeID',
    }),
  ];

  getMembershipElements = (
    roleMapFn: (roles: RoleDto[]) => (RoleDto & { disabled: boolean })[]
  ): DvDynamicFormElement[] => [
    new DvDropdownClientSideElement({
      colspan: 6,
      isClearable: true,
      closeOnSelect: false,
      isCheckboxEnabled: true,
      key: 'Roles',
      valueKey: 'RoleID',
      label: 'User.Roles',
      isMultipleSelectionEnabled: true,
      labelKeys: ['Name'],
      validators: [Validators.required],
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.Role,
          fields: ['RoleID', 'Name', 'TranslationKey', 'Enabled'],
        };
        this.restApi.setTranslatedDropdownData<
          RoleDto,
          Partial<RoleDto> & { disabled: boolean }
        >(request, params, 'TranslationKey', undefined, 'Name', roleMapFn);
      },
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'DepartmentID',
      label: 'User.Department',
      labelKeys: ['DepartmentDescription'],
      valueKey: 'DepartmentID',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.Department,
          fields: ['DepartmentID', 'DepartmentDescription'],
          filterModel: activeRecordFilter,
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      colspan: 6,
      isClearable: true,
      key: 'KeyfobSerial',
      label: 'User.KeyfobSerial',
      labelKeys: ['KeyfobSerial'],
      valueKey: 'KeyfobSerial',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.AvailableKeyfobs,
          fields: ['KeyfobSerial'],
          filterModel: {
            Active: { filterType: 'set', values: ['Active'] },
            ...QuickFilterModel.Active,
          },
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
    new DvSpaceElement({
      colspan: 6,
    }),
    new DvTextboxElement({
      colspan: 6,
      key: 'CardSerial',
      label: 'User.CardSerial',
    }),
    new DvSpaceElement({
      colspan: 6,
    }),
    new DvTextboxElement({
      key: 'CreatedTime',
      hidden: true,
    }),
    new DvDropdownClientSideElement({
      colspan: 6,
      isClearable: true,
      closeOnSelect: false,
      isCheckboxEnabled: true,
      key: 'Tags',
      label: 'User.SelectTags',
      isMultipleSelectionEnabled: true,
      valueKey: 'TagDescription',
      labelKeys: ['TagDescription'],
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.TagUser,
          fields: ['TagUserID', 'TagDescription'],
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
  ];

  getAllowedDepartmentsElements = (): DvDynamicFormElement[] => [
    new DvDropdownClientSideElement({
      colspan: 12,
      isClearable: true,
      closeOnSelect: false,
      isCheckboxEnabled: true,
      key: 'AllowDepartments',
      valueKey: 'DepartmentID',
      label: 'User.AllowedDepartments',
      isMultipleSelectionEnabled: true,
      labelKeys: ['DepartmentDescription'],
      isSelectAllEnabled: true,
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.Department,
          fields: ['DepartmentID', 'DepartmentDescription'],
          filterModel: activeRecordFilter,
        };
        this.restApi.setDropdownData(request, params);
      },
    }),
  ];

  /** Prepare User data to match editor format */
  prepareDataForForm(data: UserDto): UserDto {
    data.RegistrationDate = data.RegistrationDate?.substring(0, 10) || null;
    return data;
  }
}
